/**
 * Driver ID Constants
 * Centralized constants for all driver types to avoid hardcoded IDs throughout the application
 */

export const DRIVER_IDS = {
  INPUT_BY: 1,
  PER_OCCUPIED_ROOM: 2,
  PER_AVAILABLE_ROOM: 3,
  PERCENT_OF_ACCOUNTS: 4,
  PERCENT_OF_DEPARTMENT_REVENUE: 5,
  PER_KEY: 6,
  PERCENT_OF_TYPES: 7,
  PERCENT_OF_WAGES: 8,
  PAIRED_DRIVER: 9,
  HOURS_PER_DAY: 10,
  MINUTES_PER_ROOM: 11,
  PERCENT_OF_REVENUE_SEGMENTS: 12,
  ACCOUNT_REVERSAL: 13,
  MULTI_DRIVER: 14,
  EMPLOYEE_HOURS: 15,
  AMOUNT_PER_UNIT: 16,
  TIERED_HOURS_PER_DAY: 17,
  MINS_ROOM_AVG_LOS: 18,
  TIERED_MINUTES_PER_ROOM: 19,
  SPLIT_BY_DAY_YEARLY_INPUT: 20,
  MINUTES_PER_ROOM_TO_HOURS: 21,
  PERCENT_OF_DEPARTMENT_EXPENSES: 22,
  PERCENT_OF_DEPARTMENT_PROFIT: 23
};

/**
 * Array of percentage driver IDs
 */
export const PERCENTAGE_DRIVER_IDS = [
  DRIVER_IDS.PERCENT_OF_ACCOUNTS,
  DRIVER_IDS.PERCENT_OF_DEPARTMENT_REVENUE,
  DRIVER_IDS.PERCENT_OF_TYPES,
  DRIVER_IDS.PERCENT_OF_WAGES,
  DRIVER_IDS.PERCENT_OF_REVENUE_SEGMENTS,
  DRIVER_IDS.PERCENT_OF_DEPARTMENT_EXPENSES,
  DRIVER_IDS.PERCENT_OF_DEPARTMENT_PROFIT
];

/**
 * Multi Driver inputs whose higher-grain value is an amount divided down to lower grains.
 * Every other Multi Driver input is copied down unchanged.
 */
export const MULTI_DRIVER_SPLIT_INPUT_DRIVER_IDS = [DRIVER_IDS.INPUT_BY];

/**
 * Drivers that only support Month input inside a Multi Driver line
 */
export const MULTI_DRIVER_MONTH_ONLY_DRIVER_IDS = [DRIVER_IDS.ACCOUNT_REVERSAL];

/**
 * Array of hash type driver IDs
 */
export const HASH_TYPE_DRIVER_IDS = [
  DRIVER_IDS.PER_KEY,
  DRIVER_IDS.PAIRED_DRIVER
];

/**
 * Array of drivers that use GL codes
 */
export const GL_CODE_DRIVER_IDS = [
  DRIVER_IDS.PERCENT_OF_REVENUE_SEGMENTS,
  DRIVER_IDS.ACCOUNT_REVERSAL
];

/**
 * Array of drivers that use hours/minutes values
 */
export const TIME_BASED_DRIVER_IDS = [
  DRIVER_IDS.HOURS_PER_DAY,
  DRIVER_IDS.MINUTES_PER_ROOM,
  DRIVER_IDS.EMPLOYEE_HOURS,
  DRIVER_IDS.MINS_ROOM_AVG_LOS
];

/**
 * Array of drivers that support comp room selection
 */
export const COMP_ROOM_DRIVER_IDS = [
  DRIVER_IDS.PER_OCCUPIED_ROOM,
  DRIVER_IDS.MINUTES_PER_ROOM,
  DRIVER_IDS.EMPLOYEE_HOURS
];

/**
 * TPX-1909: labor drivers that produce an hours quantity. A non-stat line using one of
 * these can be linked to a Stats account, whose hours are then derived from this line via
 * the Minutes per Room to Hours driver (21). Note: intentionally broader than
 * TIME_BASED_DRIVER_IDS, which omits the two tiered drivers (17, 19).
 * Employee Hours (15) is intentionally excluded — linked-stat is not supported for it.
 */
export const HOURS_PRODUCING_DRIVER_IDS = [
  DRIVER_IDS.HOURS_PER_DAY,
  DRIVER_IDS.MINUTES_PER_ROOM,
  DRIVER_IDS.TIERED_HOURS_PER_DAY,
  DRIVER_IDS.MINS_ROOM_AVG_LOS,
  DRIVER_IDS.TIERED_MINUTES_PER_ROOM
];

/**
 * Driver types array for dropdowns
 */
export const DRIVER_TYPES = [
  { label: "Input by", value: String(DRIVER_IDS.INPUT_BY) },
  { label: "Per occupied room", value: String(DRIVER_IDS.PER_OCCUPIED_ROOM) },
  { label: "Per available room", value: String(DRIVER_IDS.PER_AVAILABLE_ROOM) },
  { label: "% of account(s)", value: String(DRIVER_IDS.PERCENT_OF_ACCOUNTS) },
  { label: "% of department(s) revenue", value: String(DRIVER_IDS.PERCENT_OF_DEPARTMENT_REVENUE) },
  { label: "% of department(s) expenses", value: String(DRIVER_IDS.PERCENT_OF_DEPARTMENT_EXPENSES) },
  { label: "% of department(s) profit", value: String(DRIVER_IDS.PERCENT_OF_DEPARTMENT_PROFIT) },
  { label: "Per key", value: String(DRIVER_IDS.PER_KEY) },
  { label: "% of type(s)", value: String(DRIVER_IDS.PERCENT_OF_TYPES) },
  { label: "% of wage(s)", value: String(DRIVER_IDS.PERCENT_OF_WAGES) },
  { label: "Paired driver", value: String(DRIVER_IDS.PAIRED_DRIVER) },
  { label: "Hours per day", value: String(DRIVER_IDS.HOURS_PER_DAY) },
  { label: "Minutes per room", value: String(DRIVER_IDS.MINUTES_PER_ROOM) },
  { label: "% of revenue segment(s)", value: String(DRIVER_IDS.PERCENT_OF_REVENUE_SEGMENTS) },
  { label: "Account reversal", value: String(DRIVER_IDS.ACCOUNT_REVERSAL) },
  { label: "Multi-driver (add breakdowns)", value: String(DRIVER_IDS.MULTI_DRIVER) },
  { label: "Employee hours driver", value: String(DRIVER_IDS.EMPLOYEE_HOURS) },
  { label: "Amount per unit", value: String(DRIVER_IDS.AMOUNT_PER_UNIT) },
  { label: "Tiered hours per day", value: String(DRIVER_IDS.TIERED_HOURS_PER_DAY) },
  { label: "Mins/room - avg len of stay", value: String(DRIVER_IDS.MINS_ROOM_AVG_LOS) },
  { label: "Tiered minutes per room", value: String(DRIVER_IDS.TIERED_MINUTES_PER_ROOM) },
  { label: "Split By Day", value: String(DRIVER_IDS.SPLIT_BY_DAY_YEARLY_INPUT) },
  { label: "Minutes per Room to Hours", value: String(DRIVER_IDS.MINUTES_PER_ROOM_TO_HOURS) }
];

/**
 * Available driver types for breakdown component (excluding paired driver and multi-driver)
 */
export const AVAILABLE_DRIVER_TYPES_FOR_STATISTICAL_BREAKDOWN = [
  { label: "Input by", value: String(DRIVER_IDS.INPUT_BY) },
  { label: "Minutes per Room to Hours", value: String(DRIVER_IDS.MINUTES_PER_ROOM_TO_HOURS) },
  { label: "Per key", value: String(DRIVER_IDS.PER_KEY) },
  { label: "Hours per day", value: String(DRIVER_IDS.HOURS_PER_DAY) },
  { label: "Minutes per room", value: String(DRIVER_IDS.MINUTES_PER_ROOM) },
];

export const AVAILABLE_DRIVER_TYPES_FOR_BREAKDOWN = [
  { label: "Input by", value: String(DRIVER_IDS.INPUT_BY) },
  { label: "Per occupied room", value: String(DRIVER_IDS.PER_OCCUPIED_ROOM) },
  { label: "Per available room", value: String(DRIVER_IDS.PER_AVAILABLE_ROOM) },
  { label: "% of account(s)", value: String(DRIVER_IDS.PERCENT_OF_ACCOUNTS) },
  { label: "% of department(s) revenue", value: String(DRIVER_IDS.PERCENT_OF_DEPARTMENT_REVENUE) },
  { label: "% of department(s) expenses", value: String(DRIVER_IDS.PERCENT_OF_DEPARTMENT_EXPENSES) },
  { label: "% of department(s) profit", value: String(DRIVER_IDS.PERCENT_OF_DEPARTMENT_PROFIT) },
  { label: "Per key", value: String(DRIVER_IDS.PER_KEY) },
  { label: "% of type(s)", value: String(DRIVER_IDS.PERCENT_OF_TYPES) },
  { label: "% of wage(s)", value: String(DRIVER_IDS.PERCENT_OF_WAGES) },
  { label: "Hours per day", value: String(DRIVER_IDS.HOURS_PER_DAY) },
  { label: "Minutes per room", value: String(DRIVER_IDS.MINUTES_PER_ROOM) },
  { label: "% of Revenue Segment(s)", value: String(DRIVER_IDS.PERCENT_OF_REVENUE_SEGMENTS) },
  { label: "Account reversal", value: String(DRIVER_IDS.ACCOUNT_REVERSAL) },
  { label: "Amount per unit", value: String(DRIVER_IDS.AMOUNT_PER_UNIT) },
  { label: "Tiered hours per day", value: String(DRIVER_IDS.TIERED_HOURS_PER_DAY) },
  { label: "Tiered minutes per room", value: String(DRIVER_IDS.TIERED_MINUTES_PER_ROOM) },
  { label: "Mins/room - avg len of stay", value: String(DRIVER_IDS.MINS_ROOM_AVG_LOS) }
];

